import React, { useState, useEffect } from 'react';
import { http } from '../../lib/api';
import { 
  Plus, 
  MessageSquare, 
  Clock, 
  CheckCircle,
  ChevronRight,
  AlertTriangle,
  Zap
} from 'lucide-react';
import SolicitudServicioModal from '../ordenes/SolicitudServicioModal';

export default function TicketsListCliente() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [ticketSeleccionado, setTicketSeleccionado] = useState(null);

  useEffect(() => {
    cargarTickets();
  }, []);

  const cargarTickets = async () => {
    setLoading(true);
    try {
      // El backend filtra automáticamente por cliente según el JWT
      const res = await http.get('/tickets');
      setTickets(res.data || []);
    } catch (err) {
      console.error('Error cargando tickets:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (estado) => {
    switch (estado) {
      case 'pendiente':  return { dot: 'bg-yellow-500', badge: 'bg-yellow-100 text-yellow-800', label: 'Pendiente' };
      case 'en_proceso': return { dot: 'bg-emerald-500',   badge: 'bg-emerald-100 text-emerald-800',     label: 'En Proceso' };
      case 'resuelto':   return { dot: 'bg-green-500',  badge: 'bg-green-100 text-green-800',   label: 'Resuelto' };
      case 'cerrado':    return { dot: 'bg-gray-400',   badge: 'bg-gray-100 text-gray-700',     label: 'Cerrado' };
      default:           return { dot: 'bg-gray-400',   badge: 'bg-gray-100 text-gray-700',     label: estado };
    }
  };

  const getPrioridadConfig = (prioridad) => {
    switch (prioridad) {
      case 'critica': return { label: 'Crítica', className: 'text-red-600 font-bold',       icon: <Zap size={12} className="text-red-600" /> };
      case 'alta':    return { label: 'Alta',    className: 'text-orange-600 font-semibold', icon: <AlertTriangle size={12} className="text-orange-500" /> };
      case 'media':   return { label: 'Media',   className: 'text-yellow-600', icon: null };
      case 'baja':    return { label: 'Baja',    className: 'text-green-600',  icon: null };
      default:        return { label: prioridad || 'Media', className: 'text-gray-600', icon: null };
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-5xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Soporte Técnico</h1>
            <p className="text-gray-600">Historial de solicitudes y estado en tiempo real</p>
          </div>
          <button
            onClick={() => setMostrarModal(true)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-sm"
          >
            <Plus size={20} />
            <span className="hidden sm:inline">Nueva Solicitud</span>
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
          </div>
        ) : tickets.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="bg-emerald-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <MessageSquare className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay solicitudes</h3>
            <p className="text-gray-500 mb-6">¿Tiene algún problema técnico? Cree una nueva solicitud.</p>
            <button
              onClick={() => setMostrarModal(true)}
              className="text-emerald-600 font-medium hover:text-emerald-700"
            >
              Crear primera solicitud
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {tickets.map((ticket) => {
              const statusCfg = getStatusConfig(ticket.estado);
              const priorCfg = getPrioridadConfig(ticket.prioridad);
              return (
                <div
                  key={ticket._id || ticket.id}
                  onClick={() => setTicketSeleccionado(ticket)}
                  className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all cursor-pointer group"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1 min-w-0">
                      <div className={`mt-1.5 w-2.5 h-2.5 rounded-full shrink-0 ${statusCfg.dot}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="font-semibold text-gray-900 truncate">
                            {ticket.equipo_nombre || 'Solicitud General'}
                          </span>
                          <span className="text-xs text-gray-400 font-mono">
                            #{ticket.numero_ticket || ticket.id?.slice(-8).toUpperCase()}
                          </span>
                        </div>
                        <p className="text-gray-600 text-sm line-clamp-2 mb-2">{ticket.descripcion_problema || 'Sin descripción'}</p>
                        <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Clock size={12} />
                            {ticket.fecha_creacion
                              ? new Date(ticket.fecha_creacion).toLocaleDateString('es-PY', { day: '2-digit', month: 'short', year: 'numeric' })
                              : '—'}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full ${statusCfg.badge}`}>
                            {statusCfg.label}
                          </span>
                          {ticket.prioridad && (
                            <span className={`flex items-center gap-1 ${priorCfg.className}`}>
                              {priorCfg.icon}{priorCfg.label}
                            </span>
                          )}
                          {ticket.sucursal && <span className="text-gray-400">{ticket.sucursal}</span>}
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="text-gray-300 group-hover:text-gray-500 transition-colors shrink-0 mt-1" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {mostrarModal && (
        <SolicitudServicioModal
          onClose={() => setMostrarModal(false)}
          onSuccess={() => {
            cargarTickets();
            setMostrarModal(false);
          }}
        />
      )}

      {/* Panel de detalle del ticket */}
      {ticketSeleccionado && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-0 md:p-4"
          onClick={() => setTicketSeleccionado(null)}
        >
          <div
            className="bg-white w-full md:max-w-lg rounded-t-2xl md:rounded-2xl shadow-2xl p-6 max-h-[80vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-gray-900">{ticketSeleccionado.equipo_nombre || 'Solicitud General'}</h2>
                <p className="text-sm text-gray-400 font-mono">#{ticketSeleccionado.numero_ticket || ticketSeleccionado.id?.slice(-8).toUpperCase()}</p>
              </div>
              <button onClick={() => setTicketSeleccionado(null)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Estado</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusConfig(ticketSeleccionado.estado).badge}`}>
                  {getStatusConfig(ticketSeleccionado.estado).label}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Prioridad</span>
                <span className={`flex items-center gap-1 text-xs font-medium ${getPrioridadConfig(ticketSeleccionado.prioridad).className}`}>
                  {getPrioridadConfig(ticketSeleccionado.prioridad).icon}
                  {getPrioridadConfig(ticketSeleccionado.prioridad).label}
                </span>
              </div>
              {ticketSeleccionado.sucursal && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Sucursal</span>
                  <span className="font-medium text-gray-800">{ticketSeleccionado.sucursal}</span>
                </div>
              )}
              {ticketSeleccionado.usuario_afectado && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Usuario afectado</span>
                  <span className="font-medium text-gray-800">{ticketSeleccionado.usuario_afectado}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Fecha de creación</span>
                <span className="font-medium text-gray-800">
                  {ticketSeleccionado.fecha_creacion
                    ? new Date(ticketSeleccionado.fecha_creacion).toLocaleDateString('es-PY', { day: '2-digit', month: 'long', year: 'numeric' })
                    : '—'}
                </span>
              </div>
              <div className="mt-4 border-t pt-4">
                <p className="text-sm text-gray-500 mb-1">Descripción del problema</p>
                <p className="text-gray-800 text-sm leading-relaxed">{ticketSeleccionado.descripcion_problema || 'Sin descripción'}</p>
              </div>
              {ticketSeleccionado.orden_servicio_id && (
                <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-100">
                  <div className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-green-600" />
                    <p className="text-sm text-green-800 font-medium">Orden de servicio generada</p>
                  </div>
                  <p className="text-xs text-green-600 mt-0.5">Un técnico fue asignado para resolver su solicitud.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
